import React from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';

const TermsAndConditions = ({ isOpen, onClose, onAccept }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h2 className="text-2xl font-serif font-bold text-[#1C1917]">Terms and Conditions</h2>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6 space-y-6 text-[#57534E]">
          <p className="text-sm text-stone-500">Last Updated: January 2026</p>

          <section>
            <h3 className="text-lg font-semibold text-[#1C1917] mb-3">1. Acceptance of Terms</h3>
            <p className="text-sm leading-relaxed">
              By accessing and using VowFlow ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms and Conditions, please do not use this Service.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#1C1917] mb-3">2. Service Description</h3>
            <p className="text-sm leading-relaxed mb-2">
              VowFlow is a <strong>wedding planning tool</strong> designed to assist users with organizing and managing various aspects of wedding preparation, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-sm space-y-1 ml-4">
              <li>Timeline creation and task management</li>
              <li>Guest list organization</li>
              <li>Budget planning and tracking</li>
              <li>AI-powered suggestions and recommendations</li>
              <li>Vendor management</li>
            </ul>
          </section>

          <section className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-amber-900 mb-3">⚠️ 3. Important Disclaimer</h3>
            <div className="space-y-2 text-sm">
              <p className="font-semibold text-amber-900">
                VowFlow is a PLANNING TOOL ONLY and is NOT a substitute for professional wedding planning services.
              </p>
              <p>
                <strong>We strongly recommend</strong> that you:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Consult with professional wedding planners, coordinators, and vendors</li>
                <li>Verify all information, suggestions, and recommendations independently</li>
                <li>Review contracts and agreements with legal counsel when necessary</li>
                <li>Confirm all bookings, dates, and arrangements directly with vendors</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#1C1917] mb-3">4. Limitation of Liability</h3>
            <div className="text-sm space-y-2">
              <p>
                <strong>VowFlow and its owners, operators, and affiliates shall NOT be held responsible or liable for:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Any decisions made based on information or suggestions provided by the Service</li>
                <li>Errors, omissions, or inaccuracies in AI-generated content or recommendations</li>
                <li>Any losses, damages, or disputes arising from wedding planning activities</li>
                <li>Vendor performance, quality of services, or fulfillment of contracts</li>
                <li>Missed deadlines, scheduling conflicts, or organizational oversights</li>
                <li>Financial losses resulting from budgeting decisions or vendor payments</li>
                <li>Data loss, service interruptions, or technical failures</li>
                <li>Any direct, indirect, incidental, consequential, or punitive damages</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#1C1917] mb-3">5. User Responsibility</h3>
            <p className="text-sm leading-relaxed">
              You acknowledge and agree that you are solely responsible for all wedding planning decisions, vendor selections, contractual agreements, and financial commitments. You must exercise your own judgment and due diligence in all planning activities.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#1C1917] mb-3">6. Subscription and Billing</h3>
            <div className="text-sm space-y-2">
              <p><strong>Free Trial:</strong> If you sign up with a promotional trial code, your payment method will be verified but not charged during the trial period. After the trial expires, you will be automatically charged the subscription fee for your selected plan unless you cancel before the trial ends.</p>
              <p><strong>Recurring Billing:</strong> Subscriptions renew automatically on a monthly basis. You authorize us to charge your payment method for the subscription fee each billing cycle.</p>
              <p><strong>Cancellation:</strong> You may cancel your subscription at any time. Cancellations take effect at the end of the current billing period. No refunds are provided for partial months.</p>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#1C1917] mb-3">7. Data and Privacy</h3>
            <p className="text-sm leading-relaxed">
              We collect and process personal information as described in our Privacy Policy. By using VowFlow, you consent to such processing and warrant that all data provided by you is accurate.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#1C1917] mb-3">8. Intellectual Property</h3>
            <p className="text-sm leading-relaxed">
              All content, features, and functionality of VowFlow are owned by VowFlow and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#1C1917] mb-3">9. Modifications to Terms</h3>
            <p className="text-sm leading-relaxed">
              We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting. Your continued use of the Service constitutes acceptance of any modifications.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#1C1917] mb-3">10. Governing Law</h3>
            <p className="text-sm leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="border-t border-stone-200 pt-4">
            <p className="text-sm text-stone-500 italic">
              By clicking "I Accept" below, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-stone-200 bg-stone-50">
          <Button
            onClick={onClose}
            className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700"
          >
            Cancel
          </Button>
          <Button
            onClick={onAccept}
            className="flex-1 bg-[#881337] hover:bg-[#9F1239] text-white"
          >
            I Accept Terms & Conditions
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
